import Box from 'components/elements/Box';
import ScrollTop from 'components/utility/ScrollTop/ScrollTop';
import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components/macro';
import NotificationSettingsModal from '../components/notifications/Settings';
import Content from 'components/styled/Content/Content';
import InViewObserver from 'components/utility/InViewObserver/InViewObserver';
import Layout from 'components/styled/Layout/Layout';
import TopBar from 'components/Header/TopBar/TopBar';
import Header from 'components/Header/Header';
import BackToTop from 'components/BackToTop/BackToTop';
import Footer from 'components/Footer/Footer';
import delve from 'dlv';

import {
  Header as HeaderWrapper,
  HeaderContainer,
  ContentWrapper,
  Title,
} from '../components/SystemStatus/shared/styles';
import { useDispatch, useSelector } from 'react-redux';
import { setUserVisitedVideoPage } from 'redux/reducers/user/updateUserHasVisitedVideoPage';
import { RootState } from 'redux/rootReducer';
import { IIterator } from 'types/shared';
import {
  loadOrInitUserSettings,
  shouldLoadUserSettings,
} from 'redux/reducers/userSettings';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';

const Main = styled.div.attrs({
  role: 'main',
})`
  position: relative;
  z-index: 1;
  flex: auto;
  display: flex;
  flex-flow: column nowrap;
  background-color: ${({ theme }): string => theme.LightGrey};
`;

interface IUserSettings {
  data: IIterator;
  error: any;
  errorInfo: any;
  loading: { all: boolean };
  success: { all: boolean };
}

const TrainingVideo: React.FC = () => {
  const dispatch = useDispatch();

  const userSettings = useSelector(
    (state: RootState): IUserSettings => state.userSettings
  );

  const userSettingsLoaded = useMemo(
    () => delve(userSettings, ['success', 'all'], true),
    [userSettings]
  );

  useEffect(() => {
    if (dispatch(shouldLoadUserSettings())) {
      dispatch(loadOrInitUserSettings());
    }
  }, [dispatch]);

  useEffect(() => {
    const start = new Date().getTime();

    return (): void => {
      const end = new Date().getTime();
      const totalTime = (end - start) / 1000;

      logToAmplitude('User On Training Video Page', {
        startTime: start,
        endTime: end,
        duration: totalTime,
      });
    };
  }, []);

  useEffect(() => {
    if (userSettingsLoaded) dispatch(setUserVisitedVideoPage(true));
  }, [dispatch, userSettingsLoaded]);

  return (
    <>
      <Helmet defer={false} title="Opus Training Video" />
      <ScrollTop />
      <NotificationSettingsModal />
      <InViewObserver
        render={({ inview }: any): JSX.Element => (
          <Header inView={inview} withVerticalBackground>
            <TopBar
              entityTitle="Opus Training Video"
              inView={inview}
              isSticky
              simpleTextTopBar
            />
            <Layout.MaxWidth>
              <HeaderContainer>
                <HeaderWrapper simpleTextTopBar>
                  <ContentWrapper data-pw="header-system-status">
                    <Title>Opus Training Video</Title>
                  </ContentWrapper>
                </HeaderWrapper>
              </HeaderContainer>
            </Layout.MaxWidth>
          </Header>
        )}
      />

      <Main>
        <Content>
          <Box display="flex" justifyContent="center" height="100%">
            <iframe
              width="100%"
              height="100%"
              src="https://web.microsoftstream.com/embed/video/50e18178-183e-4c19-a7a3-01171f01908d?autoplay=false&showinfo=true"
              allowFullScreen
              style={{ border: 0 }}
            ></iframe>
          </Box>
        </Content>
      </Main>

      <BackToTop />
      <Footer />
    </>
  );
};

export default TrainingVideo;
